import React from 'react';
import { useAtomValue } from 'jotai';
import {
  layoutModeAtom,
  sidebarCollapsedAtom,
  chatSidebarCollapsedAtom,
  chatSidebarWidthAtom
} from '../../../../atoms/v3/uiAtoms';

type HeaderContainerMode =
  | 'mobile'           // 移动端模式，全宽
  | 'desktop-main'     // 桌面端主内容区域，适应中栏宽度
  | 'desktop-sidebar'; // 桌面端侧边栏，适应侧边栏宽度

interface IOSHeaderContainerProps {
  children: React.ReactNode;
  isEmbedded?: boolean;
  className?: string;
  mode?: HeaderContainerMode;
}

/**
 * iOS 风格的顶部容器组件
 * 只负责提供背景、模糊、定位和安全区域适配
 * 内容布局完全由子组件决定
 * 支持桌面端侧边栏避让
 */
export const IOSHeaderContainer: React.FC<IOSHeaderContainerProps> = ({
  children,
  isEmbedded = false,
  className = '',
  mode
}) => {
  const layoutMode = useAtomValue(layoutModeAtom);
  const sidebarCollapsed = useAtomValue(sidebarCollapsedAtom);
  const chatSidebarCollapsed = useAtomValue(chatSidebarCollapsedAtom);
  const chatSidebarWidth = useAtomValue(chatSidebarWidthAtom);

  // 自动推断模式
  const containerMode = mode || (layoutMode === 'desktop' ? 'desktop-main' : 'mobile');

  // 动态计算定位和边距
  const getPositionAndMargins = () => {
    if (isEmbedded) {
      return {
        position: '',
        style: {}
      };
    }

    switch (containerMode) {
      case 'desktop-main':
        // 桌面主内容区域：固定定位，避让左右侧边栏
        return {
          position: 'fixed left-0 right-0',
          style: {
            marginLeft: sidebarCollapsed ? '4.375rem' : '16.25rem',
            marginRight: chatSidebarCollapsed ? '0' : `${chatSidebarWidth}rem`,
            transition: 'margin 300ms ease-in-out'
          }
        };

      case 'desktop-sidebar':
        // 桌面侧边栏：相对定位，适应容器宽度
        return {
          position: 'absolute left-0 right-0',
          style: {}
        };

      case 'mobile':
      default:
        // 移动端：固定定位，全宽
        return {
          position: 'fixed left-0 right-0',
          style: {}
        };
    }
  };

  const { position, style } = getPositionAndMargins();

  return (
    <div
      className={`
        ${position}
        top-0 z-20
        bg-white/80 dark:bg-ios-dark-gray6/80
        backdrop-blur-ios
        border-b border-ios-gray5 dark:border-ios-dark-gray5
        pt-[env(safe-area-inset-top)]
        ${className}
      `}
      style={style}
    >
      {children}
    </div>
  );
};
